/**
 * Wall-clock arithmetic in a zone that is not the browser's.
 *
 * A schedule belongs to a BUILDING, not to whoever is looking at it. The
 * hotel's 09:00 clean is 09:00 in the hotel's zone whether it is being
 * read from Singapore or from London, and every day/weekday/day-of-month
 * question a recurrence rule asks has to be answered on the building's
 * calendar or the rule comes out naming the wrong day.
 *
 * `Date` cannot do this: its getters are browser-local and its
 * constructor takes browser-local fields. So the two directions are
 * built here on `Intl.DateTimeFormat`, which is the only zone database
 * the platform exposes:
 *
 *   epoch -> wall clock   `zonedParts` (exact, one format call)
 *   wall clock -> epoch   `wallClockToEpochMs` (fixed-point, see below)
 *
 * Everything else in this file is one of those two with a different
 * shape on it.
 */
import { formatDate, formatDateTime } from '../../utils/date-format';

export interface WallClock {
  year: number;
  /** 1-12, NOT the 0-11 `Date` uses — these are calendar fields, and a
   *  silent off-by-one between the two conventions is exactly the class
   *  of bug this module exists to remove. */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const MS_PER_DAY = 86_400_000;

/**
 * `hourCycle: 'h23'` rather than bare `hour12: false`: the latter still
 * renders midnight as "24" under several locales, which reads back as
 * the next day. `en-US` is pinned for the same reason the parts are read
 * by type instead of by position — nothing here may depend on the
 * viewer's locale.
 */
const PARTS_FORMAT_CACHE = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = PARTS_FORMAT_CACHE.get(timeZone);
  if (cached) return cached;
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  PARTS_FORMAT_CACHE.set(timeZone, fmt);
  return fmt;
}

/** True when `timeZone` is a zone this runtime actually knows. A stored
 *  schedule can carry any string the server accepted, and a typo must
 *  not throw inside a render. */
export function isKnownTimeZone(timeZone: string): boolean {
  try {
    partsFormatter(timeZone).format(0);
    return true;
  } catch {
    return false;
  }
}

/** The browser's own zone — the operator's clock, never the site's. Used
 *  only as a labelled last resort when a site's zone is unknown. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** The wall clock `epochMs` shows on in `timeZone`. */
export function zonedParts(epochMs: number, timeZone: string): WallClock {
  const parts = partsFormatter(timeZone).formatToParts(new Date(epochMs));
  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((p) => p.type === type);
    return found ? Number(found.value) : 0;
  };
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  };
}

/**
 * The instant at which `timeZone` reads `wall`.
 *
 * Solved by iteration, not by table lookup: the offset that applies
 * depends on the instant, and the instant depends on the offset. Two
 * passes are enough for every zone on earth — the first lands within an
 * offset of the answer, the second within a DST step of it, and a zone
 * whose offset changes twice inside one such step does not exist.
 *
 * Ambiguous and nonexistent wall clocks (the hour a fall-back repeats,
 * the hour a spring-forward skips) resolve to whatever the fixed point
 * settles on, which is stable for a given input — the same property the
 * server relies on (`scheduler._to_epoch_ms`, `fold=0`). Stability is
 * what matters here: an occurrence's identity must not move under it.
 */
export function wallClockToEpochMs(wall: WallClock, timeZone: string): number {
  const target = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
  );
  let guess = target;
  for (let i = 0; i < 2; i += 1) {
    const seen = zonedParts(guess, timeZone);
    const seenUtc = Date.UTC(
      seen.year,
      seen.month - 1,
      seen.day,
      seen.hour,
      seen.minute,
      seen.second,
    );
    const drift = target - seenUtc;
    if (drift === 0) break;
    guess += drift;
  }
  return guess;
}

/** The local fields of a browser-local `Date`, read as a wall clock.
 *
 *  This is the right reading for a value that came out of a
 *  `datetime-local` input: the browser parsed the typed digits into a
 *  browser-local Date, so its LOCAL getters — and only those — give back
 *  exactly what was typed. */
export function wallClockOfLocalDate(d: Date): WallClock {
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
    second: d.getSeconds(),
  };
}

/**
 * Days since the epoch on `timeZone`'s calendar. Subtracting two of
 * these is the only correct way to ask "how many calendar days apart
 * are these two instants, for the people who live here" — differencing
 * epoch milliseconds gets a DST day wrong by an hour, and differencing
 * browser-local dates gets every cross-zone case wrong by a whole day.
 */
export function zonedDayIndex(epochMs: number, timeZone: string): number {
  const { year, month, day } = zonedParts(epochMs, timeZone);
  return Math.round(Date.UTC(year, month - 1, day) / MS_PER_DAY);
}

/** Whole calendar days from `fromMs` to `toMs` as `timeZone` counts them. */
export function zonedDayDelta(fromMs: number, toMs: number, timeZone: string): number {
  return zonedDayIndex(toMs, timeZone) - zonedDayIndex(fromMs, timeZone);
}

/** `0` = Sunday, matching `Date.prototype.getDay()` and `WEEKDAYS`. */
export function zonedWeekday(epochMs: number, timeZone: string): number {
  // `Date.UTC` of the zone-local calendar date, read back with a UTC
  // getter: a fixed date has a fixed weekday, and going through UTC
  // keeps the browser's own zone out of the answer.
  const { year, month, day } = zonedParts(epochMs, timeZone);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** `HH:mm` on `timeZone`'s clock. */
export function formatZonedClock(epochMs: number, timeZone: string): string {
  const { hour, minute } = zonedParts(epochMs, timeZone);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(hour)}:${pad(minute)}`;
}

/**
 * A `Date` whose BROWSER-LOCAL fields are the wall clock `timeZone`
 * shows at `epochMs`.
 *
 * A shim, and only for handing to `utils/date-format.ts`, whose whole
 * point is one unambiguous `DD/MMM/YYYY` form everywhere and which
 * therefore reads local getters. Re-implementing that form here to make
 * it zone-aware would put a second month table in the codebase and
 * invite exactly the per-screen drift it was written to end.
 */
export function zonedCalendarDate(epochMs: number, timeZone: string): Date {
  const { year, month, day, hour, minute, second } = zonedParts(epochMs, timeZone);
  return new Date(year, month - 1, day, hour, minute, second);
}

/** The SPA-wide `DD/MMM/YYYY` date, on `timeZone`'s calendar. */
export function formatZonedDate(epochMs: number, timeZone: string): string {
  return formatDate(zonedCalendarDate(epochMs, timeZone));
}

/** The SPA-wide `DD/MMM/YYYY, HH:mm`, on `timeZone`'s clock. */
export function formatZonedDateTime(epochMs: number, timeZone: string): string {
  return formatDateTime(zonedCalendarDate(epochMs, timeZone));
}

/**
 * How a zone is named to an operator: `Asia/Singapore (SGT)`.
 *
 * Both halves earn their place. The IANA name is the unambiguous one
 * and the only one that matches what is stored; the abbreviation is the
 * one a person recognises, and it is also the half that CHANGES across a
 * DST boundary, which is the fact a schedule's reader most needs to see.
 */
export function timeZoneLabel(timeZone: string, atMs: number = Date.now()): string {
  try {
    const short = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    })
      .formatToParts(new Date(atMs))
      .find((p) => p.type === 'timeZoneName')?.value;
    return short && short !== timeZone ? `${timeZone} (${short})` : timeZone;
  } catch {
    return timeZone;
  }
}
